import React, { useEffect, useMemo, useState } from "react";
import { useQuery } from "urql";
import { useParams, useLocation, useSearchParams } from "wouter";
import { keyBy, sortBy } from "lodash";

import { Dropdown } from "../../../../components/src/Dropdown";
import { DateHelper } from "../../../../common/src";
import { Page } from "../../../../components/src/Page";
import { useQueryState } from "../../../../components/src/use-query-state";
import { usePrevious } from "../../../../components/src/common/Utils";
import { FoNavBonds } from "./FoNavBonds";
import { FoNavCds } from "./FoNavCds";
import { FoNavDateRangeChip } from "./FoNavDateRangeChip";
import { FoNavEstimate } from "./FoNavEstimate";
import { FoNavFundNav } from "./FoNavFundNav";
import { FoNavKpis, FoNavEstimateKpiValues, FoNavKpiValues } from "./FoNavKpis";
import { FoNavFx } from "./FoNavFx";
import { FoNavPositionRisk } from "./FoNavPositionRisk";
import { FoNavOis } from "./FoNavOis";
import { FoNavSebSwaps } from "./FoNavSebSwaps";
import { FoNavSwaps } from "./FoNavSwaps";
import { DEFAULT_FUND_ID, FONAV_RATE_SOURCE_OPTIONS, FoNavRateSource } from "./constants";
import { GET_FUNDS, GET_FONAV_FUND_NAV } from "./queries";
import { parseSebSwapRatesPaste } from "./sebSwapRatesPaste";
import { getNavDailyPerformance, sumNavAfterAdjustmentsAfterOrders, sumNavBeforeAdjustmentsBeforeOrders } from "./utils";

const getPreviousBusinessDay = (date: Date): Date => {
    const day = date.getDay();
    if (day === 0) {
        return DateHelper.addDays(date, -2);
    }
    if (day === 1) {
        return DateHelper.addDays(date, -3);
    }
    if (day === 6) {
        return DateHelper.addDays(date, -1);
    }
    return DateHelper.addDays(date, -1);
};

interface SelectedFund {
    _id: string;
    name: string;
    label: string;
}

export function FoNavPage(): React.ReactElement {
    const { fundId } = useParams<{ fundId: string }>();
    const [, navigate] = useLocation();
    const previousFundId = usePrevious(fundId);

    const [, setSearchParams] = useSearchParams();
    const [startDate, setStartDate] = useQueryState("startDate", getPreviousBusinessDay(new Date()).toISOString().slice(0, 10));
    const [endDate] = useQueryState("endDate", new Date().toISOString().slice(0, 10));

    const handleEndDateChange = (newEndDate: string) => {
        const newStartDate = getPreviousBusinessDay(new Date(newEndDate)).toISOString().slice(0, 10);
        setSearchParams((prev) => {
            prev.set("endDate", newEndDate);
            prev.set("startDate", newStartDate);
            return prev;
        });
    };
    const [selectedFund, setSelectedFund] = useState<SelectedFund | null>(null);
    const [rateSource, setRateSource] = useState<FoNavRateSource>("internal");
    const [sebPastedText, setSebPastedText] = useState("");
    const sebRates = useMemo(() => parseSebSwapRatesPaste(sebPastedText), [sebPastedText]);
    const [estimateKpis, setEstimateKpis] = useState<FoNavEstimateKpiValues>({
        estimatedChangePct: null,
        estimatedNav: null,
        estimateLoading: true
    });

    useEffect(() => {
        setEstimateKpis({
            estimatedChangePct: null,
            estimatedNav: null,
            estimateLoading: true
        });
    }, [selectedFund?._id, startDate, endDate]);

    const [{ fetching: loadingFunds, error: errorFunds, data: dataFunds }] = useQuery({
        query: GET_FUNDS,
        requestPolicy: "cache-and-network"
    });

    const [{ fetching: navFetching, data: navData }] = useQuery({
        query: GET_FONAV_FUND_NAV,
        variables: {
            fundId: selectedFund?._id,
            date: startDate
        },
        pause: !selectedFund?._id || !startDate,
        requestPolicy: "network-only"
    });

    const [{ data: endNavData }] = useQuery({
        query: GET_FONAV_FUND_NAV,
        variables: {
            fundId: selectedFund?._id,
            date: endDate
        },
        pause: !selectedFund?._id || !endDate,
        requestPolicy: "network-only"
    });

    const prevNav = useMemo(() => {
        return sumNavAfterAdjustmentsAfterOrders(navData?.previousNav?.[0]?.data?.classes);
    }, [navData]);

    const prevNavDate = navData?.previousNav?.[0]?.date ?? null;

    // There is usually no saved NAV for the end date yet, which is exactly what this page estimates.
    let currentNavDate: string | null = null;
    if (endNavData?.previousNav?.[0]?.date === endDate) {
        currentNavDate = endDate;
    }

    const kpis = useMemo((): FoNavKpiValues => {
        const endDateNav = endNavData?.previousNav?.[0];
        let actualChangePct: number | null = null;
        let actualNav: number | null = null;
        let actualDate: string | null = null;
        if (endDateNav && endDateNav.date === endDate) {
            actualChangePct = getNavDailyPerformance(endDateNav.data?.classes);
            actualNav = sumNavBeforeAdjustmentsBeforeOrders(endDateNav.data?.classes);
            actualDate = endDateNav.date;
        }

        const prevNavBeforeOrdersBeforeFee = sumNavBeforeAdjustmentsBeforeOrders(navData?.previousNav?.[0]?.data?.classes);

        return {
            prevNav,
            prevNavDate,
            prevNavBeforeOrdersBeforeFee,
            estimatedChangePct: estimateKpis.estimatedChangePct,
            estimatedNav: estimateKpis.estimatedNav,
            estimateLoading: estimateKpis.estimateLoading,
            actualChangePct,
            actualNav,
            actualDate
        };
    }, [prevNav, prevNavDate, navData, estimateKpis, endNavData, endDate]);

    const isInvalidDateRange = new Date(startDate) > new Date(endDate);

    const fundQueryString = useMemo(() => {
        const params = new URLSearchParams();
        if (startDate) {
            params.set("startDate", startDate);
        }
        if (endDate) {
            params.set("endDate", endDate);
        }
        const query = params.toString();
        return query ? `?${query}` : "";
    }, [startDate, endDate]);

    useEffect(() => {
        if (!dataFunds?.funds) {
            return;
        }

        const fundsById = keyBy(dataFunds.funds, "_id");

        if (fundId && fundsById[fundId]) {
            const fund = fundsById[fundId];
            setSelectedFund({ _id: fund._id, name: fund.name, label: fund.name });
        } else if (!fundId) {
            if (fundsById[DEFAULT_FUND_ID]) {
                navigate(`/fo-nav-beta/${DEFAULT_FUND_ID}${fundQueryString}`, { replace: true });
            }
        } else if (fundId !== previousFundId) {
            navigate(`/fo-nav-beta/${DEFAULT_FUND_ID}${fundQueryString}`, { replace: true });
        }
    }, [dataFunds, fundId, previousFundId, navigate, fundQueryString]);

    useEffect(() => {
        if (selectedFund) {
            document.title = `Front-office NAV - ${selectedFund.name}`;
        }
    }, [selectedFund]);

    if (errorFunds) {
        return (
            <div>
                <p>error:</p>
                <pre>{JSON.stringify(errorFunds, null, 2)}</pre>
            </div>
        );
    }

    if (loadingFunds || !dataFunds) {
        return <p>Loading funds...</p>;
    }

    const fundOptions = sortBy(
        dataFunds.funds.map((fund) => ({
            _id: fund._id,
            label: fund.name
        })),
        "label"
    );

    return (
        <Page className="fonavpage">
            <h3>Front-office NAV</h3>

            <div className="flex flex-wrap items-end gap-3 mb-4">
                <div>
                    <div className="text-[11px] uppercase tracking-wider text-slate-500 font-medium mb-1">Fund</div>
                    <Dropdown>
                        <Dropdown.Toggle variant="light" id="selectFoNavFund">
                            {selectedFund ? selectedFund.label : "Select fund"}
                        </Dropdown.Toggle>
                        <Dropdown.Menu>
                            {fundOptions.map((option) => (
                                <Dropdown.Item
                                    key={option._id}
                                    onClick={() => {
                                        navigate(`/fo-nav-beta/${option._id}${fundQueryString}`);
                                    }}
                                >
                                    {option.label}
                                </Dropdown.Item>
                            ))}
                        </Dropdown.Menu>
                    </Dropdown>
                </div>
                <FoNavDateRangeChip
                    startDate={startDate}
                    endDate={endDate}
                    onChangeStart={setStartDate}
                    onChangeEnd={handleEndDateChange}
                />
                <div>
                    <div className="text-[11px] uppercase tracking-wider text-slate-500 font-medium mb-1">Swap rates</div>
                    <div className="inline-flex rounded border border-slate-300 overflow-hidden">
                        {FONAV_RATE_SOURCE_OPTIONS.map((option) => (
                            <button
                                key={option.value}
                                type="button"
                                onClick={() => setRateSource(option.value)}
                                className={
                                    rateSource === option.value
                                        ? "px-3 py-1 text-sm bg-slate-700 text-white"
                                        : "px-3 py-1 text-sm bg-white text-slate-700 hover:bg-slate-100"
                                }
                            >
                                {option.label}
                            </button>
                        ))}
                    </div>
                </div>
            </div>

            {rateSource === "seb" && sebRates.length === 0 ? (
                <div className="warningDateRange mb-4">
                    <p>Warning: external independent source selected but nothing has been pasted, so swap changes are empty</p>
                </div>
            ) : null}

            {isInvalidDateRange ? (
                <div className="warningDateRange mb-4">
                    <p>Warning: end date is before start date</p>
                </div>
            ) : null}

            {!isInvalidDateRange && selectedFund ? (
                <div className="flex flex-wrap -mx-2">
                    <div className="w-full px-2">
                        <FoNavKpis kpis={kpis} />
                    </div>
                    <div className="w-full lg:w-6/12 md:w-6/12 sm:w-full px-2 mb-4">
                        <FoNavEstimate
                            fundId={selectedFund._id}
                            startDate={startDate}
                            endDate={endDate}
                            prevNavDate={prevNavDate}
                            currentNavDate={currentNavDate}
                            totalNav={prevNav}
                            rateSource={rateSource}
                            sebRates={sebRates}
                            onEstimateChange={setEstimateKpis}
                        />
                    </div>
                    <div className="w-full lg:w-6/12 md:w-6/12 sm:w-full px-2 mb-4">
                        <div className="mb-4">
                            <FoNavFundNav fundId={selectedFund._id} startDate={startDate} />
                        </div>
                        <FoNavPositionRisk fundId={selectedFund._id} startDate={startDate} endDate={endDate} />
                        <FoNavFx startDate={startDate} endDate={endDate} />
                        <FoNavBonds startDate={startDate} endDate={endDate} />
                        <FoNavCds startDate={startDate} endDate={endDate} />
                    </div>
                    <div className="w-full px-2 mb-4">
                        <FoNavSwaps startDate={startDate} endDate={endDate} />
                    </div>
                    <div className="w-full px-2 mb-4">
                        <FoNavOis startDate={startDate} endDate={endDate} />
                    </div>
                    <div className="w-full lg:w-6/12 md:w-6/12 sm:w-full px-2 mb-4">
                        <FoNavSebSwaps
                            endDate={endDate}
                            pastedText={sebPastedText}
                            sebRates={sebRates}
                            onPastedTextChange={setSebPastedText}
                        />
                    </div>
                </div>
            ) : null}
        </Page>
    );
}
